import { Music, ShoppingBag, Utensils, Package, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { EVENT } from "@/config";

const SCHEDULE = [
  { time: "7:00 AM", title: "Warm-Up & Opening Remarks", desc: "Group stretch, a word from Hope Restored Missions, and the national anthem." },
  { time: "7:25 AM", title: "1 Mile Fun Walk Begins", desc: "Untimed and all-ages — strollers and leashed pets welcome." },
  { time: "8:00 AM", title: "5K Timed Run Starts", desc: "Chip-timed start on the certified 3.13-mile course. Live results all morning." },
  { time: "8:30 AM", title: "Food Truck, Music & Vendor Village", desc: "Celebrate at the finish festival with the food truck, music, and local vendors." },
  { time: "9:30 AM", title: "Awards Ceremony", desc: "Age-group medals for the timed 5K, presented at the pavilion." },
];

const FEST = [
  { icon: Utensils, label: "Food Truck" },
  { icon: Music, label: "Music" },
  { icon: ShoppingBag, label: "Vendors" },
];

export const Timeline = () => (
  <section id="schedule" data-testid="schedule-section" className="bg-hope-cream py-24 md:py-32">
    <div className="mx-auto grid max-w-7xl gap-16 px-4 md:px-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-hope-green">
            Race Day Timeline
          </p>
          <h2
            data-testid="schedule-heading"
            className="font-heading text-4xl font-black leading-[1.05] tracking-tighter text-hope-ink sm:text-5xl"
          >
            Saturday,
            <br />
            November 21<span className="text-hope-red">.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-600 md:text-lg">
            {EVENT.venue} — {EVENT.address}. Festivities begin at 7:00 AM;
            stick around for the finish-line festival after your race.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="packet-pickup-card"
            className="mt-10 max-w-md rounded-3xl border-2 border-hope-green/30 bg-white p-7"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-hope-green/10 text-hope-green">
                <Package className="h-5 w-5" />
              </span>
              <h3 className="font-heading text-xl font-bold text-hope-ink">Packet Pickup</h3>
            </div>
            <p className="mt-4 font-heading text-lg font-bold text-hope-green">
              Wednesday–Friday, Nov 18–20 · 10 AM – 4 PM
            </p>
            <p className="mt-2 flex items-start gap-2 text-sm leading-relaxed text-zinc-600">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-hope-green" />
              Hope Restored Missions, 197 Avenue K Suite B100, Plano, Texas 75074
            </p>
            <p className="mt-3 text-sm text-zinc-500">
              No race-morning pickup — please grab your packet during the week.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="schedule-festivities">
            {FEST.map((f) => (
              <span
                key={f.label}
                className="inline-flex items-center gap-2 rounded-full border border-hope-green/30 bg-white px-4 py-2 text-sm font-semibold text-hope-ink"
              >
                <f.icon className="h-4 w-4 text-hope-green" />
                {f.label}
              </span>
            ))}
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <div className="relative border-l-2 border-hope-green/30 pl-8 md:pl-12" data-testid="schedule-list">
          {SCHEDULE.map((s, i) => (
            <Reveal key={s.time + s.title} delay={i * 0.08}>
              <div data-testid={`schedule-item-${i}`} className="relative pb-10 last:pb-0">
                <span className="absolute -left-[41px] top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-hope-green bg-white md:-left-[57px]">
                  <span className={`h-2 w-2 rounded-full ${s.title.includes("5K") ? "bg-hope-red" : "bg-hope-green"}`} />
                </span>
                <p className="font-heading text-sm font-bold tracking-[0.15em] text-hope-green">{s.time}</p>
                <h3 className="mt-1 font-heading text-xl font-bold text-hope-ink md:text-2xl">{s.title}</h3>
                <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-zinc-600">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-xs text-zinc-500" data-testid="schedule-disclaimer">
            Times subject to final race-day briefing. Rain or shine, we run.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
